import React, { useEffect, useMemo, useRef } from 'react';

import { graphql, useFragment } from 'react-relay';

import { UserProfilePreview } from '@atlassian/user-profile-preview';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { useAnalytics } from '../../common/controller/analytics/use-analytics';
import { onPersonKnowledgeCardLoaded } from '../on-person-knowledge-card-loaded';

import type {
	singlePersonKnowledgeCardRelay_reportingLines$data,
	singlePersonKnowledgeCardRelay_reportingLines$key,
} from './__generated__/singlePersonKnowledgeCardRelay_reportingLines.graphql';
import type { singlePersonKnowledgeCardRelay_smarts$key } from './__generated__/singlePersonKnowledgeCardRelay_smarts.graphql';
import type { singlePersonKnowledgeCardRelay_smarts$data } from './__generated__/singlePersonKnowledgeCardRelay_smarts.graphql';
import type { singlePersonKnowledgeCardRelay_user$key } from './__generated__/singlePersonKnowledgeCardRelay_user.graphql';
import type { singlePersonKnowledgeCardRelay_workedOn$data } from './__generated__/singlePersonKnowledgeCardRelay_workedOn.graphql';
import type { singlePersonKnowledgeCardRelay_workedOn$key } from './__generated__/singlePersonKnowledgeCardRelay_workedOn.graphql';
import { confluenceLinksToAbsoluteUrl } from './confluence-links-to-absolute-url';

type Collaborator = {
	accountId: string;
	name: string;
	avatarUrl?: string | null;
	jobTitle?: string | null;
	email?: string | null;
};

type RecentWorkUrls = string[];

type HierarchyUser = {
	accountId: string;
	name: string;
	avatarUrl?: string | null;
	jobTitle?: string | null;
	numberOfDirectReports?: number;
	numberOfTotalReports?: number;
};

type HierarchyData = {
	managers?: HierarchyUser[];
	user: HierarchyUser;
	reports?: HierarchyUser[];
	peers?: HierarchyUser[];
};

type SinglePersonKnowledgeCardRelayProps = Omit<
	React.ComponentProps<typeof UserProfilePreview>,
	'recentWorkData'
> & {
	userFragment?: singlePersonKnowledgeCardRelay_user$key;
	workedOnFragment?: singlePersonKnowledgeCardRelay_workedOn$key;
	smartsFragment?: singlePersonKnowledgeCardRelay_smarts$key;
	reportingLinesFragment?: singlePersonKnowledgeCardRelay_reportingLines$key;
};

export function mapRelayWorkedOnConnectionToRecentWorkItems(
	data: singlePersonKnowledgeCardRelay_workedOn$data,
): RecentWorkUrls {
	const result: string[] = [];

	for (const edge of data.edges) {
		const item = edge?.node;
		const object = item?.object;
		if (!object) {
			continue;
		}

		const objectData = object.data;
		if (!objectData) {
			continue;
		}

		let url = '';

		if ('links' in objectData && objectData.links) {
			url = confluenceLinksToAbsoluteUrl(objectData.links.base, objectData.links.webUi);
		} else if (objectData.__typename === 'JiraIssue') {
			url = objectData.webUrl ?? '';
		} else if (objectData.__typename === 'ExternalDocument') {
			url = objectData.url ?? '';
		} else if (objectData.__typename === 'BitbucketPullRequest') {
			url = objectData.href ?? '';
		} else if (objectData.__typename === 'LoomVideo') {
			url = objectData.loomUrl ?? '';
		}

		if (!url) {
			continue;
		}
		result.push(url);
	}

	return result;
}

type singlePersonKnowledgeCardRelay_reportingLinesKd = Extract<
	singlePersonKnowledgeCardRelay_reportingLines$data['reportingLines'],
	{ __typename: 'KnowledgeDiscoveryReportingLines' }
>;

/** `user` / `managers` / `reports` rows — same shape as `singlePersonKnowledgeCardRelay_reportingLines` fragment data. */
function mapKdReportingLinesUser(
	u: singlePersonKnowledgeCardRelay_reportingLinesKd['user'],
): HierarchyData['user'] | null {
	if (!u?.accountIdentifier) {
		return null;
	}
	return {
		accountId: u.accountIdentifier,
		name: u.pii?.name ?? '',
		avatarUrl: u.pii?.picture ?? null,
		jobTitle: u.pii?.extendedProfile?.jobTitle ?? null,
		numberOfDirectReports: u.numberOfDirectReports ?? undefined,
		numberOfTotalReports: u.numberOfTotalReports ?? undefined,
	};
}

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export function mapKnowledgeDiscoveryReportingLinesToHierarchyData(
	data: singlePersonKnowledgeCardRelay_reportingLines$data | null | undefined,
): HierarchyData | undefined {
	if (!data?.reportingLines) {
		return undefined;
	}
	const rl = data.reportingLines;
	if (rl.__typename !== 'KnowledgeDiscoveryReportingLines') {
		return undefined;
	}
	const user = mapKdReportingLinesUser(rl.user);
	if (!user) {
		return undefined;
	}
	const managers = (rl.managers ?? [])
		.map((manager) => mapKdReportingLinesUser(manager))
		.filter((manager): manager is HierarchyData['user'] => manager != null);
	const reports =
		rl.reports
			?.map((report) => mapKdReportingLinesUser(report))
			.filter((report): report is HierarchyData['user'] => report != null) ?? [];
	const peers =
		rl.peers
			?.map((peer) => mapKdReportingLinesUser(peer))
			.filter((peer): peer is HierarchyData['user'] => peer != null) ?? [];
	return {
		managers: managers.length ? managers : undefined,
		user,
		reports: reports.length ? reports : undefined,
		peers: peers.length ? peers : undefined,
	};
}

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export function mapRelaySmartsToCollaborators(
	data: singlePersonKnowledgeCardRelay_smarts$data,
	accountId?: string,
): Collaborator[] {
	const recommended = data.recommendedUser ?? [];
	if (!recommended.length) {
		return [];
	}

	return recommended.reduce<Collaborator[]>((acc, node) => {
		const user = node?.user;
		if (!user) {
			return acc;
		}
		const id = user.accountId;
		if (!id || (accountId && id === accountId)) {
			return acc;
		}
		acc.push({
			accountId: id ?? null,
			name: user.name ?? null,
			avatarUrl: user.picture ?? null,
			email: user.email ?? null,
			jobTitle: user.extendedProfile?.jobTitle ?? null,
		});
		return acc;
	}, []);
}

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export function SinglePersonKnowledgeCardRelay({
	userFragment,
	workedOnFragment,
	smartsFragment,
	reportingLinesFragment,
	...props
}: SinglePersonKnowledgeCardRelayProps): React.JSX.Element {
	const userProfileData = useFragment(
		graphql`
			fragment singlePersonKnowledgeCardRelay_user on KnowledgeDiscoveryQueryApi
			@argumentDefinitions(
				accountId: { type: "String!" }
				requestingUserId: { type: "String!" }
				tenantId: { type: "String!" }
			) {
				singleUser(accountId: $accountId, requestingUserId: $requestingUserId, tenantId: $tenantId)
					@optIn(to: "KnowledgeDiscovery Single User") {
					__typename
					... on KnowledgeDiscoverySingleUser {
						user {
							id
							name
							picture
							email
							jobTitle
							department
							location
							organization
							thirdPartyIntegrations {
								externalId
								integrationId
								product
							}
						}
					}
				}
			}
		`,
		userFragment ?? null,
	);

	const workedOnData = useFragment(
		graphql`
			fragment singlePersonKnowledgeCardRelay_workedOn on ActivityConnection {
				edges {
					node {
						object {
							data {
								__typename
								... on ConfluencePage {
									links {
										base
										webUi
									}
								}
								... on ConfluenceBlogPost {
									links {
										base
										webUi
									}
								}
								... on ConfluenceWhiteboard {
									links {
										base
										webUi
									}
								}
								... on JiraIssue {
									webUrl
								}
								... on ExternalDocument {
									url
								}
								... on BitbucketPullRequest {
									href
								}
								... on LoomVideo {
									loomUrl: url
								}
							}
						}
					}
				}
			}
		`,
		workedOnFragment ?? null,
	);

	const smartsData = useFragment(
		graphql`
			fragment singlePersonKnowledgeCardRelay_smarts on SmartsQueryApi
			@argumentDefinitions(recommendationsQuery: { type: "SmartsRecommendationsQuery!" }) {
				recommendedUser(recommendationsQuery: $recommendationsQuery) {
					user {
						accountId
						name
						picture
						... on AtlassianAccountUser {
							email
							extendedProfile {
								jobTitle
							}
						}
					}
				}
			}
		`,
		smartsFragment ?? null,
	);

	const reportingLinesData = useFragment(
		graphql`
			fragment singlePersonKnowledgeCardRelay_reportingLines on KnowledgeDiscoveryQueryApi
			@argumentDefinitions(
				aaid: { type: "String!" }
				orgId: { type: "String!" }
				siteId: { type: "String!" }
			) {
				reportingLines(aaid: $aaid, orgId: $orgId, siteId: $siteId)
					@optIn(to: "KnowledgeDiscovery Get reporting lines") {
					__typename
					... on KnowledgeDiscoveryReportingLines {
						user {
							accountIdentifier
							numberOfDirectReports
							numberOfTotalReports
							pii {
								name
								picture
								extendedProfile {
									jobTitle
								}
							}
						}
						managers {
							accountIdentifier
							numberOfDirectReports
							numberOfTotalReports
							pii {
								name
								picture
								extendedProfile {
									jobTitle
								}
							}
						}
						reports {
							accountIdentifier
							numberOfDirectReports
							numberOfTotalReports
							pii {
								name
								picture
								extendedProfile {
									jobTitle
								}
							}
						}
						peers {
							accountIdentifier
							numberOfDirectReports
							numberOfTotalReports
							pii {
								name
								picture
								extendedProfile {
									jobTitle
								}
							}
						}
					}
				}
			}
		`,
		reportingLinesFragment ?? null,
	);

	const relayHierarchyData = useMemo(
		() => mapKnowledgeDiscoveryReportingLinesToHierarchyData(reportingLinesData),
		[reportingLinesData],
	);

	const recentWorkData = useMemo(() => {
		if (!workedOnData) {
			return undefined;
		}
		const mapped = mapRelayWorkedOnConnectionToRecentWorkItems(workedOnData);
		return mapped.length ? mapped : undefined;
	}, [workedOnData]);

	const relayProfileData = useMemo(() => {
		const result = userProfileData?.singleUser;
		if (!result || result.__typename !== 'KnowledgeDiscoverySingleUser') {
			return undefined;
		}
		const user = result.user;
		if (!user?.id) {
			return undefined;
		}
		return {
			accountId: user.id,
			name: user.name ?? '',
			avatarUrl: user.picture ?? null,
			email: user.email ?? null,
			jobTitle: user.jobTitle ?? null,
			department: user.department ?? null,
			location: user.location ?? null,
			organization: user.organization ?? null,
			thirdPartyIntegrations:
				user.thirdPartyIntegrations?.map((integration) => ({
					externalId: integration.externalId,
					integrationId: integration.integrationId,
					product: integration.product,
				})) ?? null,
		};
	}, [userProfileData]);

	const relayCollaborators = useMemo(() => {
		if (!smartsData) {
			return undefined;
		}
		const mapped = mapRelaySmartsToCollaborators(smartsData, relayProfileData?.accountId);
		return mapped.length ? mapped : undefined;
	}, [smartsData, relayProfileData?.accountId]);

	const { commonAttributes } = useAIAnswerContext();
	const [fireAnalyticsEvent] = useAnalytics();
	const hasFiredLoadedEventRef = useRef(false);

	// Fire the `aiAnswerPersonKnowledgeCard loaded` track event once per mount, after Relay
	// resolves. This component only renders inside a Suspense+ErrorBoundary, so reaching here
	// always means a successful data load — errors are caught by the boundary above.
	useEffect(() => {
		if (hasFiredLoadedEventRef.current) {
			return;
		}
		hasFiredLoadedEventRef.current = true;
		fireAnalyticsEvent(
			onPersonKnowledgeCardLoaded({
				...commonAttributes,
				personCardVariant: 'singlePerson',
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<UserProfilePreview
			{...props}
			profileData={relayProfileData ?? props.profileData}
			hierarchyData={relayHierarchyData ?? props.hierarchyData}
			recentWorkData={recentWorkData}
			collaborators={relayCollaborators ?? props.collaborators}
		/>
	);
}
